import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Paper,
  Badge,
  useMantineColorScheme,
  Center,
  Loader,
  ActionIcon,
  Tooltip,
  Modal,
  Image,
} from '@mantine/core';
import type { MantineColorScheme } from '@mantine/core';
import { IconPlayerPause, IconPlayerPlay, IconDeviceFloppy, IconX, IconInfoCircle , IconBook, IconTag } from '@tabler/icons-react';
import examenService from '../services/examenService';
import {
  IniciarExamenResponse,
  RespuestaUsuario,
  ExamenEnProgreso
} from '../types/examen.types';
import QuejaSugerenciaButton from '../components/QuejaSugerenciaButton';
import RecuperarExamenModal from '../components/RecuperarExamenModal';
import { authService } from '../services/authService';
import { examenStorage } from '../utils/examenStorage';

// Stable OptionCard component to prevent remounts on timer updates
const OptionCard: React.FC<{
  value: string;
  label: React.ReactNode;
  selected: boolean;
  disabled?: boolean;
  onSelect: (value: string) => void;
  colorScheme: MantineColorScheme;
  palette: 'blue' | 'amber';
}> = ({ value, label, selected, disabled, onSelect, colorScheme, palette }) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  const isInteractive = !disabled && !selected;
  
  // Reset hover state when selection or disabled changes
  useEffect(() => {
    if (selected || disabled) {
      setHovered(false);
      setFocused(false);
    }
  }, [selected, disabled]);

  const colors = palette === 'amber'
    ? {
        hoverBgDark: 'rgba(245, 158, 11, 0.12)',
        hoverBgLight: 'rgba(245, 158, 11, 0.06)',
        selectedBgDark: 'rgba(245, 158, 11, 0.14)',
        selectedBgLight: 'rgba(245, 158, 11, 0.10)',
        selectedBorderDark: '#fbbf24',
        selectedBorderLight: '#f59e0b',
        hoverBorder: '#fbbf24',
        hoverShadowDark: '0 10px 24px rgba(0,0,0,0.35)',
        hoverShadowLight: '0 8px 20px rgba(245, 158, 11, 0.20)'
      }
    : {
        hoverBgDark: 'rgba(59, 130, 246, 0.10)',
        hoverBgLight: 'rgba(59, 130, 246, 0.06)',
        selectedBgDark: 'rgba(59, 130, 246, 0.12)',
        selectedBgLight: 'rgba(59, 130, 246, 0.10)',
        selectedBorderDark: '#93c5fd',
        selectedBorderLight: '#3b82f6',
        hoverBorder: '#60a5fa',
        hoverShadowDark: '0 10px 24px rgba(0,0,0,0.35)',
        hoverShadowLight: '0 8px 20px rgba(59,130,246,0.15)'
      };

  const bgColor = selected
    ? (colorScheme === 'dark' ? colors.selectedBgDark : colors.selectedBgLight)
    : (hovered || focused)
      ? (colorScheme === 'dark' ? colors.hoverBgDark : colors.hoverBgLight)
      : (colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#ffffff');

  const borderClr = selected
    ? (colorScheme === 'dark' ? colors.selectedBorderDark : colors.selectedBorderLight)
    : (hovered || focused)
      ? colors.hoverBorder
      : undefined;

  const boxSh = selected
    ? 'none'
    : (hovered || focused)
      ? (colorScheme === 'dark' ? colors.hoverShadowDark : colors.hoverShadowLight)
      : '0 0 0 rgba(0,0,0,0)';

  const transform = selected ? 'none' : (hovered ? 'translateY(-2px)' : 'none');

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onSelect(value)}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect(value);
        }
      }}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderColor: borderClr,
        borderWidth: selected ? 2 : 1,
        boxShadow: boxSh,
        backgroundColor: bgColor,
        transition: 'background-color 150ms ease, border-color 150ms ease, border-width 150ms ease, box-shadow 150ms ease, transform 150ms ease',
        userSelect: 'none',
        transform,
      }}
      onMouseEnter={() => isInteractive && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => isInteractive && setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <Group align="flex-start" gap="md" wrap="nowrap">
        <Badge
          size="md"
          variant={selected ? 'filled' : 'light'}
          color={selected ? 'dark' : 'gray'}
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '14px',
          }}
        >
          {value.toUpperCase()}
        </Badge>
        <Text style={{ lineHeight: 1.5, flex: 1, textAlign: 'left' }}>{label}</Text>
      </Group>
    </Paper>
  );
};

const ExamenFiltradoPage: React.FC = () => {
  console.log('🚀🚀🚀 [EXAMEN_FILTRADO] ====== COMPONENTE MONTADO ======');

  const navigate = useNavigate();
  const location = useLocation();
  const { colorScheme } = useMantineColorScheme();
  const currentUser = authService.getCurrentUserFromStorage();

  console.log('📍 [EXAMEN_FILTRADO] Location state al montar:', location.state);
  console.log('👤 [EXAMEN_FILTRADO] Usuario actual:', currentUser?.username);

  const [examenData, setExamenData] = useState<IniciarExamenResponse | null>(
    location.state?.examenData || null
  );
  const [preguntaActual, setPreguntaActual] = useState(0);
  const [respuestas, setRespuestas] = useState<Map<number, string | null>>(new Map());
  const [tiempoInicio, setTiempoInicio] = useState<Date | null>(null);
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pausado, setPausado] = useState(false);
  const [modalFinalizarAbierto, setModalFinalizarAbierto] = useState(false);
  const [finalizando, setFinalizando] = useState(false); // Estado para detener auto-guardado
  // Selección temporal (no guardada) de la pregunta actual
  const [seleccionTemporal, setSeleccionTemporal] = useState<string | null>(null);

  // Estado para recuperación de examen
  const [examenEnProgreso, setExamenEnProgreso] = useState<ExamenEnProgreso | null>(null);
  const [mostrarModalRecuperar, setMostrarModalRecuperar] = useState(false);
  const [cargandoRecuperacion, setCargandoRecuperacion] = useState(false);
  const [verificandoProgreso, setVerificandoProgreso] = useState(true);
  // Cuando se viene del simulador con "Continuar", recuperar directo sin mostrar el modal
  const [recuperarDirecto, setRecuperarDirecto] = useState(false);
  const yaVerificado = useRef(false);
  
  // Modal de confirmación tipo "ingresar a examen" al pausar y guardar
  const [mostrarModalGuardado, setMostrarModalGuardado] = useState(false);

  // Modal para otro examen en progreso (de otro tipo)
  const [mostrarModalOtroExamen, setMostrarModalOtroExamen] = useState(false);
  const [otroExamen, setOtroExamen] = useState<ExamenEnProgreso | null>(null);
  const verificarExamenEnProgreso = useCallback(async () => {
    console.log('🔎 [PAGE_VERIFICAR] ====== INICIO ======');

    // Evitar múltiples verificaciones
    if (yaVerificado.current) {
      console.log('⚠️ [PAGE_VERIFICAR] Ya se verificó anteriormente, saltando...');
      return;
    }
    yaVerificado.current = true;

    console.log('🚀 [PAGE_VERIFICAR] Iniciando verificación de examen en progreso');
    console.log('📍 [PAGE_VERIFICAR] Usuario actual:', currentUser?.id, '-', currentUser?.username);
    console.log('📍 [PAGE_VERIFICAR] Location state:', {
      continuandoExamen: location.state?.continuandoExamen,
      tieneExamenData: !!location.state?.examenData,
      recuperando: location.state?.recuperando,
      tieneExamenEnProgreso: !!location.state?.examenEnProgreso
    });

    if (!currentUser) {
      console.error('❌ [PAGE_VERIFICAR] No hay usuario actual');
      alert('No se pudo obtener el usuario actual');
      navigate('/');
      return;
    }

    // PRIMERA PRIORIDAD: Si viene desde el simulador para continuar
    if (location.state?.continuandoExamen && location.state?.examenEnProgreso) {
      console.log('🔄 [PAGE_VERIFICAR] PRIORIDAD 1: Continuando examen desde simulador');
      console.log('📦 [PAGE_VERIFICAR] Datos del examen en progreso:');
      console.log('   - IntentoId:', location.state.examenEnProgreso.intentoId);
      console.log('   - Tipo:', location.state.examenEnProgreso.tipoExamen);
      console.log('🔧 [PAGE_VERIFICAR] Configurando estado...');
      setExamenEnProgreso(location.state.examenEnProgreso);
      if (location.state?.continuarDirecto) {
        setRecuperarDirecto(true); // El efecto disparará recuperarExamen al tener el examen
      } else {
        setMostrarModalRecuperar(true);
      }
      setVerificandoProgreso(false);
      console.log('✅ [PAGE_VERIFICAR] Modal configurado para mostrarse');
      console.log('🎉 [PAGE_VERIFICAR] ====== FIN (Desde simulador) ======');
      return; // IMPORTANTE: Salir aquí y NO continuar con las otras prioridades
    }

    // SEGUNDA PRIORIDAD: Si ya tenemos examenData del state
    if (location.state?.examenData) {
      console.log('📄 [PAGE_VERIFICAR] PRIORIDAD 2: Usando examenData del state');
      if (location.state?.recuperando) {
        console.log('🔧 [PAGE_VERIFICAR] Modo recuperación desde filtros');
        // Es una recuperación desde filtros, restaurar estado
        const progreso = location.state.progreso;
        console.log('📊 [PAGE_VERIFICAR] Progreso a restaurar:', progreso);
        setPreguntaActual(progreso.preguntaActual);
        setTiempoTranscurrido(progreso.tiempoTranscurrido);
        setPausado(progreso.pausado || false);

        // Restaurar respuestas
        const respuestasMap = new Map<number, string | null>();
        Object.entries(progreso.respuestas).forEach(([orden, respuesta]) => {
          respuestasMap.set(parseInt(orden), respuesta as string);
        });
        setRespuestas(respuestasMap);
        console.log('✅ [PAGE_VERIFICAR] Respuestas restauradas:', respuestasMap.size);

        // Calcular tiempo de inicio ajustado
        const tiempoAjustado = new Date(Date.now() - progreso.tiempoTranscurrido * 1000);
        setTiempoInicio(tiempoAjustado);

        console.log('✅ [PAGE_VERIFICAR] Estado recuperado desde filtros');
      } else {
        console.log('🆕 [PAGE_VERIFICAR] Modo examen nuevo desde filtros');
        // Es un examen nuevo desde filtros
        setTiempoInicio(new Date());
        console.log('✅ [PAGE_VERIFICAR] Examen nuevo iniciado');
      }
      setVerificandoProgreso(false);
      console.log('🎉 [PAGE_VERIFICAR] ====== FIN (Con examenData) ======');
      return;
    }

    // TERCERA PRIORIDAD: Solo si no hay nada en el state, verificar servidor
    console.log('🚑 [PAGE_VERIFICAR] PRIORIDAD 3: FALLBACK - Verificando servidor porque no hay datos en state');

    try {
      // Verificar autenticación antes de hacer la petición
      console.log('🔐 [PAGE_VERIFICAR] Verificando autenticación...');
      if (!authService.isAuthenticated()) {
        console.error('❌ [PAGE_VERIFICAR] Usuario no autenticado, redirigiendo al login');
        navigate('/login?expired=true');
        return;
      }
      console.log('✅ [PAGE_VERIFICAR] Usuario autenticado');

      console.log('🌐 [PAGE_VERIFICAR] Llamando al servidor...');
      // Verificar solo exámenes FILTRADO en progreso (permitiendo otros tipos simultáneamente)
      const examen = await examenService.verificarExamenEnProgresoPorTipo(currentUser.id, 'FILTRADO');
      console.log('📡 [PAGE_VERIFICAR] Respuesta recibida del servidor:', examen);
      if (examen) {
        console.log('✅ [PAGE_VERIFICAR] Servidor devolvió examen FILTRADO:', examen.tipoExamen);
        setExamenEnProgreso(examen);
        setMostrarModalRecuperar(true);
        setVerificandoProgreso(false);
        console.log('🎉 [PAGE_VERIFICAR] ====== FIN (Modal mostrado) ======');
      } else {
        console.log('📭 [PAGE_VERIFICAR] Servidor no devolvió examen');
        // No hay examen en progreso y tampoco vino del state
        setVerificandoProgreso(false);
        console.log('🔀 [PAGE_VERIFICAR] Navegando a filtros (sin examen)');
        // Redirigir a filtros si no hay examen
        navigate('/estudiante/simulador/filtros');
      }
    } catch (error: any) {
      console.error('❌ [PAGE_VERIFICAR] ERROR en verificación:');
      console.error('   - Tipo:', error.constructor?.name);
      console.error('   - Mensaje:', error.message);
      console.error('   - Status:', error.response?.status);
      console.error('   - Data:', error.response?.data);
      setVerificandoProgreso(false);

      // Si es un error 401 (no autorizado), redirigir al login
      if (error.response?.status === 401) {
        console.log('🔐 [PAGE_VERIFICAR] Token expirado (401), redirigiendo al login');
        authService.clearTokens();
        navigate('/login?expired=true');
        return;
      }

      // Para otros errores, redirigir a filtros
      console.log('🔀 [PAGE_VERIFICAR] Error genérico, navegando a filtros');
      console.log('🚨 [PAGE_VERIFICAR] ====== ERROR ======');
      navigate('/estudiante/simulador/filtros');
    }
  }, [navigate, location, currentUser]);

  // Función para guardar progreso (se declara antes de los useEffect que la usan)
  const guardarProgresoActual = useCallback(async () => {
    if (!examenData) return;

    try {
      // Convertir Map a objeto
      const respuestasObj: Record<number, string> = {};
      respuestas.forEach((valor, clave) => {
        if (valor) respuestasObj[clave] = valor;
      });

      const request = {
        intentoId: examenData.intentoId,
        preguntaActual,
        tiempoTranscurrido,
        pausado,
        respuestas: respuestasObj
      };

      // Guardar en backend
      await examenService.guardarProgreso(request);

      // Guardar en localStorage como backup
      examenStorage.guardarProgreso(request);

      console.log('💾 Progreso guardado');
    } catch (error) {
      console.error('Error guardando progreso:', error);
    }
  }, [examenData, preguntaActual, tiempoTranscurrido, pausado, respuestas]);

  // Verificar examen en progreso al cargar - SOLO UNA VEZ, SIN DEPENDENCIAS
  useEffect(() => {
    console.log('🔎 useEffect inicial ejecutándose');
    verificarExamenEnProgreso();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // SIN DEPENDENCIAS para evitar loops

  // Continuar directo desde el simulador: al tener el examen en progreso, recuperarlo sin modal
  useEffect(() => {
    if (recuperarDirecto && examenEnProgreso) {
      setRecuperarDirecto(false);
      recuperarExamen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recuperarDirecto, examenEnProgreso]);

  useEffect(() => {
    if (examenData) {
      if (!tiempoInicio) {
        setTiempoInicio(new Date());
      }
    }
  }, [examenData]);

  // Sincronizar selección temporal cuando cambia la pregunta
  useEffect(() => {
    if (!examenData) return;
    const p = examenData.preguntas[preguntaActual];
    if (!p) return;
    // Sincronizar solo cuando cambia la pregunta, no cuando cambian las respuestas
    setSeleccionTemporal(prev => {
      const guardada = respuestas.get(p.orden);
      return guardada ?? null;
    });
  }, [examenData, preguntaActual]);

  // Timer para el examen
  useEffect(() => {
    if (!tiempoInicio || pausado) return;

    const interval = setInterval(() => {
      const ahora = new Date();
      const diferencia = Math.floor((ahora.getTime() - tiempoInicio.getTime()) / 1000);
      setTiempoTranscurrido(diferencia);
    }, 1000);

    return () => clearInterval(interval);
  }, [tiempoInicio, pausado]);

  // Auto-guardar progreso cada 30 segundos (detener si está finalizando)
  useEffect(() => {
    if (!examenData || !tiempoInicio || finalizando) return;

    const interval = setInterval(() => {
      guardarProgresoActual();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [examenData, tiempoInicio, guardarProgresoActual, finalizando]);

  const togglePausa = () => {
    if (pausado) {
      const tiempoPausado = tiempoTranscurrido;
      setTiempoInicio(new Date(Date.now() - tiempoPausado * 1000));
    } else {
      setTiempoTranscurrido(tiempoTranscurrido);
      guardarProgresoActual(); // Guardar inmediatamente al pausar
    }
    setPausado(!pausado);
  };
  const recuperarExamen = async () => {
    console.log('🔎 [PAGE_RECUPERAR] ====== INICIO ====== ');
    console.log('📥 [PAGE_RECUPERAR] Iniciando recuperación de examen');

    if (!examenEnProgreso) {
      console.error('❌ [PAGE_RECUPERAR] No hay examen en progreso para recuperar');
      return;
    }

    console.log('📦 [PAGE_RECUPERAR] Datos del examen a recuperar:');
    console.log('   - IntentoId:', examenEnProgreso.intentoId);
    console.log('   - Tipo:', examenEnProgreso.tipoExamen);
    console.log('   - Nombre:', examenEnProgreso.nombreExamen);
    console.log('   - Progreso:', examenEnProgreso.preguntaActual + 1, '/', examenEnProgreso.totalPreguntas);

    // Verificar si el examen está 100% completado
    const estaCompletado = (examenEnProgreso.preguntaActual + 1) >= examenEnProgreso.totalPreguntas;
    if (estaCompletado) {
      console.log('🎯 [PAGE_RECUPERAR] Examen 100% completado, finalizando automáticamente...');
      setCargandoRecuperacion(true);
      try {
        const progreso = await examenService.recuperarProgreso(examenEnProgreso.intentoId);
        
        // Restaurar respuestas para finalizar
        const respuestasMap = new Map<number, string | null>();
        Object.entries(progreso.respuestas).forEach(([orden, respuesta]) => {
          respuestasMap.set(parseInt(orden), respuesta);
        });
        
        // Crear el array de respuestas para finalizar
        const respuestasArray: RespuestaUsuario[] = progreso.examenData.preguntas.map((preg) => ({
          orden: preg.orden,
          respuesta: respuestasMap.get(preg.orden) || null,
          tiempoSeg: Math.floor(progreso.tiempoTranscurrido / progreso.examenData.totalPreguntas)
        }));

        const resultado = await examenService.finalizarExamen({
          intentoId: examenEnProgreso.intentoId,
          duracionSeg: progreso.tiempoTranscurrido,
          respuestas: respuestasArray
        });

        // Limpiar progreso guardado
        examenStorage.limpiarProgreso();

        console.log('✅ [PAGE_RECUPERAR] Examen finalizado, redirigiendo a resultados');
        navigate(`/estudiante/resultados/${examenEnProgreso.intentoId}`, { state: { resultado } });
      } catch (error) {
        console.error('❌ [PAGE_RECUPERAR] Error al finalizar examen completado:', error);
        alert('Error al finalizar el examen.');
      } finally {
        setCargandoRecuperacion(false);
      }
      return;
    }

    setCargandoRecuperacion(true);
    try {
      // Verificar que el usuario siga autenticado antes de continuar
      console.log('🔐 [PAGE_RECUPERAR] Verificando autenticación...');
      if (!authService.isAuthenticated()) {
        console.error('❌ [PAGE_RECUPERAR] Sesión expirada');
        alert('Tu sesión ha expirado. Serás redirigido al login.');
        authService.clearTokens();
        navigate('/login?expired=true');
        return;
      }
      console.log('✅ [PAGE_RECUPERAR] Usuario autenticado');

      console.log('🌐 [PAGE_RECUPERAR] Llamando recuperarProgreso...');
      const progreso = await examenService.recuperarProgreso(examenEnProgreso.intentoId);

      console.log('✅ [PAGE_RECUPERAR] Progreso recibido, restaurando estado...');
      console.log('📊 [PAGE_RECUPERAR] Estado a restaurar:');
      console.log('   - Pregunta actual:', progreso.preguntaActual);
      console.log('   - Tiempo transcurrido:', progreso.tiempoTranscurrido, 's');
      console.log('   - Pausado:', progreso.pausado);
      console.log('   - Respuestas:', Object.keys(progreso.respuestas).length);

      // Restaurar estado completo
      console.log('🔧 [PAGE_RECUPERAR] Restaurando examenData...');
      setExamenData(progreso.examenData);
      console.log('🔧 [PAGE_RECUPERAR] Restaurando pregunta actual:', progreso.preguntaActual);
      setPreguntaActual(progreso.preguntaActual);
      console.log('🔧 [PAGE_RECUPERAR] Restaurando tiempo transcurrido:', progreso.tiempoTranscurrido);
      setTiempoTranscurrido(progreso.tiempoTranscurrido);
      console.log('🔧 [PAGE_RECUPERAR] Restaurando pausado:', progreso.pausado || false);
      setPausado(progreso.pausado || false);

      // Restaurar respuestas
      console.log('🔧 [PAGE_RECUPERAR] Restaurando respuestas...');
      const respuestasMap = new Map<number, string | null>();
      Object.entries(progreso.respuestas).forEach(([orden, respuesta]) => {
        respuestasMap.set(parseInt(orden), respuesta);
      });
      setRespuestas(respuestasMap);
      console.log('✅ [PAGE_RECUPERAR] Respuestas restauradas:', respuestasMap.size);

      // Calcular tiempo de inicio ajustado
      const tiempoAjustado = new Date(Date.now() - progreso.tiempoTranscurrido * 1000);
      console.log('🔧 [PAGE_RECUPERAR] Tiempo de inicio ajustado:', tiempoAjustado.toISOString());
      setTiempoInicio(tiempoAjustado);

      console.log('🚪 [PAGE_RECUPERAR] Cerrando modal...');
      setMostrarModalRecuperar(false);

      console.log('🎉 [PAGE_RECUPERAR] ====== ÉXITO ======');
      console.log('✅ [PAGE_RECUPERAR] Examen recuperado exitosamente. Usuario puede continuar.');
    } catch (error: any) {
      console.error('❌ [PAGE_RECUPERAR] ERROR CAPTURADO:');
      console.error('   - Tipo:', error.constructor.name);
      console.error('   - Mensaje:', error.message);
      console.error('   - Response status:', error.response?.status);
      console.error('   - Response data:', error.response?.data);
      console.log('🚨 [PAGE_RECUPERAR] ====== ERROR ======');
      alert('Error al recuperar el examen');
    } finally {
      setCargandoRecuperacion(false);
    }
  };

  const comenzarNuevoExamen = async () => {
    if (!examenEnProgreso) return;

    setCargandoRecuperacion(true);
    try {
      // Abandonar el examen anterior
      await examenService.abandonarExamen(examenEnProgreso.intentoId);
      examenStorage.limpiarProgreso();

      setMostrarModalRecuperar(false);
      // Redirigir a filtros para crear nuevo
      navigate('/estudiante/simulador/filtros');
    } catch (error) {
      console.error('Error abandonando examen:', error);
      alert('Error al abandonar el examen anterior');
    } finally {
      setCargandoRecuperacion(false);
    }
  };

  const seleccionarRespuesta = (respuesta: string) => {
    // Solo actualizar la selección temporal; NO guardar aún
    setSeleccionTemporal(respuesta);
  };

  const siguientePregunta = () => {
    if (!examenData) return;

    if (preguntaActual < examenData.preguntas.length - 1) {
      const nextIndex = preguntaActual + 1;
      const nextP = examenData.preguntas[nextIndex];
      setSeleccionTemporal(respuestas.get(nextP.orden) ?? null);
      setPreguntaActual(nextIndex);
    }
  };

  const preguntaAnterior = () => {
    if (preguntaActual > 0) {
      const prevIndex = preguntaActual - 1;
      const prevP = examenData!.preguntas[prevIndex];
      setSeleccionTemporal(respuestas.get(prevP.orden) ?? null);
      setPreguntaActual(prevIndex);
    }
  };

  const pausarYGuardar = async () => {
    if (!examenData) return;

    setLoading(true);
    try {
      // Convertir Map a objeto
      const respuestasObj: Record<number, string> = {};
      respuestas.forEach((valor, clave) => {
        if (valor) respuestasObj[clave] = valor;
      });

      const request = {
        intentoId: examenData.intentoId,
        preguntaActual,
        tiempoTranscurrido,
        pausado: true, // Marcar como pausado
        respuestas: respuestasObj
      };

      // Guardar en backend
      await examenService.guardarProgreso(request);

      // Guardar en localStorage como backup
      examenStorage.guardarProgreso(request);

      // Actualizar estado local
      setPausado(true);

      console.log('💾 Progreso guardado y examen pausado');

      // Mostrar modal personalizado en lugar de alert
      setMostrarModalGuardado(true);
    } catch (error) {
      console.error('Error al pausar y guardar:', error);
      alert('Error al guardar el progreso del examen.');
    } finally {
      setLoading(false);
    }
  };

  const confirmarFinalizarExamen = async () => {
    if (!examenData || !tiempoInicio) return;

    setFinalizando(true); // Detener auto-guardado
    setModalFinalizarAbierto(false);
    setLoading(true);
    try {
      const respuestasArray: RespuestaUsuario[] = examenData.preguntas.map((pregunta) => ({
        orden: pregunta.orden,
        respuesta: respuestas.get(pregunta.orden) || null,
        tiempoSeg: Math.floor(tiempoTranscurrido / examenData.totalPreguntas)
      }));

      const resultado = await examenService.finalizarExamen({
        intentoId: examenData.intentoId,
        duracionSeg: tiempoTranscurrido,
        respuestas: respuestasArray
      });

      // Limpiar progreso guardado
      examenStorage.limpiarProgreso();

      navigate(`/estudiante/resultados/${examenData.intentoId}`, { state: { resultado } });
    } catch (error) {
      console.error('❌ Error al finalizar examen:', error);
      alert('Error al finalizar el examen.');
    } finally {
      setLoading(false);
    }
  };

  const formatearTiempo = (segundos: number): string => {
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  console.log('🖼️ [PAGE_RENDER] Estado actual:', {
    verificandoProgreso,
    tieneExamenData: !!examenData,
    tieneExamenEnProgreso: !!examenEnProgreso,
    mostrarModal: mostrarModalRecuperar
  });

  if (verificandoProgreso) {
    console.log('🖼️ [PAGE_RENDER] Mostrando loader de verificación');
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Stack align="center">
          <Loader size="lg" />
          <Text>Verificando progreso...</Text>
        </Stack>
      </Center>
    );
  }

  // IMPORTANTE: Si hay examen en progreso y se debe mostrar el modal, NO mostrar el loader
  // Permitir que el modal se renderice aunque no haya examenData todavía
  if (!examenData && !mostrarModalRecuperar) {
    console.log('🖼️ [PAGE_RENDER] No hay examenData ni modal, mostrando loader');
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Stack align="center">
          <Loader size="lg" />
          <Text>Cargando examen...</Text>
        </Stack>
      </Center>
    );
  }

  // Si debe mostrar modal pero no hay examenData, mostrar un contenedor vacío con el modal
  if (!examenData && mostrarModalRecuperar) {
    console.log('🖼️ [PAGE_RENDER] Mostrando solo modal (sin examenData aún)');
    return (
      <>
        <RecuperarExamenModal
          examen={examenEnProgreso}
          opened={mostrarModalRecuperar}
          onContinuar={recuperarExamen}
          onNuevo={comenzarNuevoExamen}
          loading={cargandoRecuperacion}
        />
        <Center style={{ minHeight: '100vh' }}>
          <Stack align="center">
            <Loader size="lg" />
            <Text>Preparando examen...</Text>
          </Stack>
        </Center>
      </>
    );
  }

  // Si no hay examenData en este punto, no deberíamos estar aquí (seguridad)
  if (!examenData) {
    console.error('❌ [PAGE_RENDER] Estado inválido: no hay examenData');
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Stack align="center">
          <Text c="red">Error: No hay datos del examen</Text>
          <Button onClick={() => navigate('/estudiante/simulador')}>
            Volver al Simulador
          </Button>
        </Stack>
      </Center>
    );
  }

  // Validar que hay preguntas y que el índice actual es válido
  if (!examenData.preguntas || examenData.preguntas.length === 0) {
    console.error('❌ [PAGE_RENDER] El examen no tiene preguntas');
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Stack align="center">
          <Text c="red">Error: El examen no tiene preguntas</Text>
          <Text size="sm" c="dimmed">
            Por favor, verifica que seleccionaste especialidades válidas con contenido disponible.
          </Text>
          <Button onClick={() => navigate('/estudiante/simulador/filtros')}>
            Volver a Configurar Examen
          </Button>
        </Stack>
      </Center>
    );
  }

  // Validar que el índice de pregunta actual es válido
  if (preguntaActual < 0 || preguntaActual >= examenData.preguntas.length) {
    console.error('❌ [PAGE_RENDER] Índice de pregunta inválido:', preguntaActual);
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Stack align="center">
          <Text c="red">Error: Índice de pregunta inválido</Text>
          <Button onClick={() => navigate('/estudiante/simulador')}>
            Volver al Simulador
          </Button>
        </Stack>
      </Center>
    );
  }

  const pregunta = examenData.preguntas[preguntaActual];
  const respuestaSeleccionada = seleccionTemporal;


  return (
    <>
      <RecuperarExamenModal
        examen={examenEnProgreso}
        opened={mostrarModalRecuperar}
        onContinuar={recuperarExamen}
        onNuevo={comenzarNuevoExamen}
        loading={cargandoRecuperacion}
      />

      {/* Modal de confirmación para finalizar */}
      <Modal
        opened={modalFinalizarAbierto}
        onClose={() => setModalFinalizarAbierto(false)}
        centered
        size="md"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{
          content: {
            borderRadius: '16px',
            padding: '28px',
          },
        }}
      >
        <Stack gap="xl">
          {/* Header */}
          <Box>
            <Title order={3} mb="xs" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
              {respuestas.size === examenData?.totalPreguntas ? 'Finalizar Examen' : 'Resumen del Examen'}
            </Title>
            <Text size="sm" c="dimmed">
              {respuestas.size === examenData?.totalPreguntas 
                ? 'Has completado todas las preguntas. ¿Deseas enviar el examen?' 
                : 'Revisa tu progreso antes de finalizar'}
            </Text>
          </Box>

          {/* Progreso */}
          <Box>
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Text size="sm" fw={600}>Progreso del examen</Text>
                <Group gap="sm">
                  <Badge 
                    size="lg" 
                    variant="filled"
                    color={respuestas.size === examenData?.totalPreguntas ? 'green' : 'orange'}
                    style={{ fontSize: '14px', padding: '8px 16px' }}
                  >
                    {respuestas.size} / {examenData?.totalPreguntas}
                  </Badge>
                  {examenData && respuestas.size < examenData.totalPreguntas && (
                    <Tooltip
                      label="Haz clic para ir a la pregunta, o finalizar ahora sin contestar."
                      multiline
                      w={280}
                      withArrow
                      position="bottom-end"
                    >
                      <Box style={{ cursor: 'help', display: 'flex', alignItems: 'center' }}>
                        <IconInfoCircle size={18} style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }} />
                      </Box>
                    </Tooltip>
                  )}
                </Group>
              </Group>
              
              {examenData && respuestas.size < examenData.totalPreguntas && (
                <>
                  <Text size="sm" c="orange" fw={500}>
                    Tienes {examenData.totalPreguntas - respuestas.size} {(examenData.totalPreguntas - respuestas.size) === 1 ? 'pregunta' : 'preguntas'} sin responder
                  </Text>
                  <Box
                    style={{
                      maxHeight: '140px',
                      overflowY: 'auto',
                      padding: '12px',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                    }}
                  >
                    <Text size="xs" c="dimmed" mb="sm" fw={600}>Preguntas sin responder:</Text>
                    <Group gap="xs">
                      {examenData.preguntas.map((p, idx) => {
                        if (!respuestas.has(p.orden)) {
                          return (
                            <Badge
                              key={idx}
                              size="md"
                              variant="light"
                              color="gray"
                              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                              onClick={() => {
                                setModalFinalizarAbierto(false);
                                setPreguntaActual(idx);
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                            >
                              Pregunta {idx + 1}
                            </Badge>
                          );
                        }
                        return null;
                      })}
                    </Group>
                  </Box>
                </>
              )}
              
              {respuestas.size === examenData?.totalPreguntas && (
                <Text size="sm" c="green" fw={500}>
                  Has contestado todas las preguntas
                </Text>
              )}
            </Stack>
          </Box>

          {/* Botones */}
          <Group justify="flex-end" gap="sm" mt="md">
            <Button
              variant="default"
              onClick={() => setModalFinalizarAbierto(false)}
              radius="md"
              size="sm"
            >
              Regresar
            </Button>
            <Button
              variant="filled"
              color={examenData && respuestas.size < examenData.totalPreguntas ? 'red' : 'dark'}
              onClick={confirmarFinalizarExamen}
              loading={loading}
              radius="md"
              size="sm"
            >
              {examenData && respuestas.size < examenData.totalPreguntas ? 'Sí, finalizar de todos modos' : 'Finalizar Examen'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal: otro examen en progreso (de otro tipo) */}
      <Modal
        opened={mostrarModalOtroExamen}
        onClose={() => setMostrarModalOtroExamen(false)}
        centered
        size="sm"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{ content: { borderRadius: '16px' } }}
      >
        <Stack gap="lg">
          <Box>
            <Title order={4} mb="xs">Tienes un examen en progreso</Title>
            <Text size="sm" c="dimmed">
              {otroExamen?.nombreExamen}. ¿Deseas ir a ese examen?
            </Text>
          </Box>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" radius="md" onClick={() => { setMostrarModalOtroExamen(false); navigate('/estudiante/simulador'); }}>
              Ir al simulador
            </Button>
            <Button variant="filled" color="dark" radius="md" onClick={() => {
              if (!otroExamen) return;
              if (otroExamen.tipoExamen === 'RAPIDO') navigate('/estudiante/simulador/rapido');
              else if (otroExamen.tipoExamen === 'ENARM') navigate('/estudiante/simulador/completo');
            }}>
              Ir a ese examen
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal de confirmación al pausar/guardar */}
      <Modal
        opened={mostrarModalGuardado}
        onClose={() => setMostrarModalGuardado(false)}
        centered
        size="sm"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{
          content: { borderRadius: '16px' },
        }}
      >
        <Stack gap="lg">
          <Box>
            <Title order={4} mb="xs">Examen pausado</Title>
            <Text size="sm" c="dimmed">
              Examen pausado y guardado. Puedes continuar desde donde lo dejaste.
            </Text>
          </Box>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" radius="md" onClick={() => setMostrarModalGuardado(false)}>
              Cancelar
            </Button>
            <Button variant="filled" color="dark" radius="md" onClick={() => navigate('/estudiante/simulador')}>
              Ir al simulador
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Box
        style={{
          minHeight: '100vh',
          background:
            colorScheme === 'dark'
              ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
              : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
          padding: '24px',
        }}
      >
        <Paper
          shadow="md"
          radius="md"
          p="xl"
          withBorder
          style={{
            backgroundColor:
              colorScheme === 'dark' ? '#1e293b' : 'rgba(255,255,255,0.95)',
            maxWidth: '900px',
            margin: '0 auto'
          }}
        >
          {/* Header */}
          <Group justify="space-between" align="center" mb="xl">
            <Box style={{ textAlign: 'left' }}>
              <Title order={2} mb={4}>Examen Personalizado</Title>
              <Text size="sm" c="dimmed">{examenData.totalPreguntas} preguntas</Text>
            </Box>
            
            {/* Mensaje de pausa (centrado) */}
            {pausado && (
              <Text size="10px" c="red" fw={500} style={{ flex: 1, textAlign: 'center' }}>
                Pausado - Reanuda para continuar
              </Text>
            )}
            
            {/* Timer, pausa y guardar */}
            <Group gap="sm">
              <Badge 
                color={colorScheme === 'dark' ? 'gray' : 'dark'} 
                variant="filled" 
                size="xl"
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  padding: '10px 16px',
                }}
              >
                {formatearTiempo(tiempoTranscurrido)}
              </Badge>
              <Tooltip label={pausado ? "Reanudar" : "Pausar"}>
                <ActionIcon
                  size="xl"
                  variant="filled"
                  color={colorScheme === 'dark' ? 'gray' : 'dark'}
                  onClick={togglePausa}
                >
                  {pausado ? <IconPlayerPlay size={20} /> : <IconPlayerPause size={20} />}
                </ActionIcon>
              </Tooltip>
              
              {/* Guardar y salir (solo icono) */}
              <Tooltip label="Guardar y salir">
                <ActionIcon
                  size="xl"
                  variant="filled"
                  color={colorScheme === 'dark' ? 'gray' : 'dark'}
                  onClick={pausarYGuardar}
                  disabled={loading}
                >
                  <IconDeviceFloppy size={20} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>

          {/* Caso de estudio (si aplica) */}
          {pregunta.tipo === 'CASO' && pregunta.casoEstudioContenido && (
            <Paper
              p="md"
              mb="lg"
              withBorder
              style={{
                backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#fafafa',
                borderLeft: `3px solid ${colorScheme === 'dark' ? 'rgba(148,163,184,0.4)' : '#e5e7eb'}`,
              }}
            >
              <Text fw={500} size="sm" mb="xs" c="dimmed">
                CASO CLÍNICO
              </Text>
              <Text style={{ whiteSpace: 'pre-wrap' }}>
                {pregunta.casoEstudioContenido}
              </Text>
              {pregunta.imagenCaso && (
                <Image
                  src={pregunta.imagenCaso}
                  alt="Imagen del caso clínico"
                  radius="md"
                  mt="sm"
                  maw="100%"
                />
              )}
            </Paper>
          )}

          {/* Pregunta */}
          <Box mb="sm">
            <Group justify="space-between" align="flex-start" mb="xs">
              <Title order={4} style={{ flex: 1, textAlign: 'left' }}>
                {preguntaActual + 1}. {pregunta.pregunta}
              </Title>

              {currentUser && (
                <QuejaSugerenciaButton
                  reactivoId={pregunta.tipo === 'REACTIVO' ? pregunta.reactivoId : undefined}
                  preguntaCasoId={pregunta.tipo === 'CASO' ? pregunta.preguntaCasoId : undefined}
                  tipoObjetivo={pregunta.tipo === 'CASO' ? 'PREGUNTA_CASO' : 'REACTIVO'}
                />
              )}
            </Group>

            {/* Especialidad y Tema Badges */}
            <Group gap="xs" style={{ marginTop: '8px' }}>
              {pregunta.especialidadNombre && (
                <Badge
                  size="md"
                  variant="light"
                  color="blue"
                  leftSection={<IconBook size={13} />}
                >
                  {pregunta.especialidadNombre}
                </Badge>
              )}
              {pregunta.temaNombre && (
                <Badge
                  size="md"
                  variant="light"
                  color="cyan"
                  leftSection={<IconTag size={13} />}
                >
                  {pregunta.temaNombre}
                </Badge>
              )}
            </Group>
          </Box>

          {/* Opciones - estilo tarjetas */}
          <Stack gap="sm" mb="xl" style={{ opacity: pausado ? 0.5 : 1, pointerEvents: pausado ? 'none' : 'auto' }}>
            <OptionCard
              key={`opt-a-${preguntaActual}`}
              value="a"
              label={pregunta.respuestaA}
              selected={respuestaSeleccionada === 'a'}
              disabled={pausado}
              onSelect={seleccionarRespuesta}
              colorScheme={colorScheme}
              palette="amber"
            />
            <OptionCard
              key={`opt-b-${preguntaActual}`}
              value="b"
              label={pregunta.respuestaB}
              selected={respuestaSeleccionada === 'b'}
              disabled={pausado}
              onSelect={seleccionarRespuesta}
              colorScheme={colorScheme}
              palette="amber"
            />
            <OptionCard
              key={`opt-c-${preguntaActual}`}
              value="c"
              label={pregunta.respuestaC}
              selected={respuestaSeleccionada === 'c'}
              disabled={pausado}
              onSelect={seleccionarRespuesta}
              colorScheme={colorScheme}
              palette="amber"
            />
            <OptionCard
              key={`opt-d-${preguntaActual}`}
              value="d"
              label={pregunta.respuestaD}
              selected={respuestaSeleccionada === 'd'}
              disabled={pausado}
              onSelect={seleccionarRespuesta}
              colorScheme={colorScheme}
              palette="amber"
            />
          </Stack>

          {/* Footer con navegación y progreso */}
          <Box mt="xl" pt="xl" style={{ borderTop: `1px solid ${colorScheme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
            {/* Indicadores de progreso (dots) - Con indicador de respuestas */}
            <Group justify="center" gap={8} mb="lg">
              {examenData.preguntas.map((p, index) => {
                const tieneRespuesta = respuestas.has(p.orden);
                return (
                  <Tooltip key={index} label={`Pregunta ${index + 1}`}>
                    <Box
                      style={{
                        width: index === preguntaActual ? 32 : 10,
                        height: 10,
                        borderRadius: 5,
                        backgroundColor:
                          index === preguntaActual
                            ? colorScheme === 'dark' ? '#f59e0b' : '#d97706'
                            : tieneRespuesta
                            ? colorScheme === 'dark' ? '#10b981' : '#059669'
                            : colorScheme === 'dark' ? '#475569' : '#cbd5e1',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        border: index === preguntaActual ? `2px solid ${colorScheme === 'dark' ? '#fbbf24' : '#b45309'}` : 'none',
                        opacity: tieneRespuesta ? 1 : 0.5,
                      }}
                      onClick={() => {
                        const targetP = examenData.preguntas[index];
                        setSeleccionTemporal(respuestas.get(targetP.orden) ?? null);
                        setPreguntaActual(index);
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Group>

            {/* Barra de información y controles */}
            <Group justify="space-between" align="center" wrap="nowrap">
              {/* Navegación */}
              <Group gap="xs" style={{ flex: 1, justifyContent: 'flex-start' }}>
                <ActionIcon
                  size="lg"
                  variant="default"
                  onClick={preguntaAnterior}
                  disabled={preguntaActual === 0}
                  style={{
                    opacity: preguntaActual === 0 ? 0.3 : 1,
                  }}
                >
                  ←
                </ActionIcon>
                
                <ActionIcon
                  size="lg"
                  variant="default"
                  onClick={siguientePregunta}
                  disabled={preguntaActual >= examenData.totalPreguntas - 1}
                  style={{
                    opacity: preguntaActual >= examenData.totalPreguntas - 1 ? 0.3 : 1,
                  }}
                >
                  →
                </ActionIcon>
              </Group>

              {/* Info central */}
              <Box style={{ display: 'flex', justifyContent: 'center', minWidth: '80px' }}>
                <Badge color="gray" variant="light" size="lg">
                  {preguntaActual + 1} / {examenData.totalPreguntas}
                </Badge>
              </Box>

              {/* Botones de acción a la derecha.
                  En la última pregunta solo se muestra "Finalizar" (que además
                  guarda la respuesta seleccionada antes de abrir el modal). */}
              <Group gap="sm" style={{ flex: 1, justifyContent: 'flex-end' }}>
                {preguntaActual === examenData.totalPreguntas - 1 ? (
                  <Button
                    variant="filled"
                    color="dark"
                    size="sm"
                    onClick={() => {
                      if (!examenData || !pregunta) return;
                      // Guardar la selección de la última pregunta antes de finalizar.
                      const nuevas = new Map(respuestas);
                      if (seleccionTemporal) nuevas.set(pregunta.orden, seleccionTemporal);
                      else nuevas.delete(pregunta.orden);
                      setRespuestas(nuevas);
                      guardarProgresoActual();
                      setModalFinalizarAbierto(true);
                    }}
                    disabled={loading}
                    radius="md"
                  >
                    Finalizar
                  </Button>
                ) : (
                  <Button
                    variant="light"
                    color="dark"
                    size="sm"
                    onClick={() => {
                      if (!examenData || !pregunta) return;
                      const nuevas = new Map(respuestas);
                      if (seleccionTemporal) nuevas.set(pregunta.orden, seleccionTemporal);
                      else nuevas.delete(pregunta.orden);
                      setRespuestas(nuevas);
                      guardarProgresoActual();
                      const nextIndex = preguntaActual + 1;
                      const nextP = examenData.preguntas[nextIndex];
                      setSeleccionTemporal(nuevas.get(nextP.orden) ?? null);
                      setPreguntaActual(nextIndex);
                    }}
                    disabled={loading}
                    radius="md"
                  >
                    Guardar respuesta
                  </Button>
                )}
              </Group>
            </Group>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default ExamenFiltradoPage;
